import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Chip, Typography, FormControl, InputLabel, Select, MenuItem, IconButton } from '@mui/material';
import { 
  Add as AddIcon, 
  Refresh as RefreshIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Work as WorkIcon
} from '@mui/icons-material';

import ProjectService from '../services/projectService';
import ClientService from '../../clients/services/clientService';
import PageHeader from '../../../components/common/PageHeader';
import SearchHeader from '../../../components/common/SearchHeader';
import ResponsiveTable from '../../../components/common/ResponsiveTable';
import PaginationComponent from '../../../components/common/PaginationComponent';
import DeleteConfirmDialog from '../../../components/common/DeleteConfirmDialog';
import FormBuilder from '../../../components/common/FormBuilder';
import ItemCard from '../../../components/common/ItemCard';
import { createProjectSchema, updateProjectSchema } from '../schemas/projectFormSchema';

const ProjectListingPage = () => {
  // Data states
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalProjects, setTotalProjects] = useState(0);
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Sorting states
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Load clients for dropdown
  const loadClients = useCallback(async () => {
    try {
      const result = await ClientService.getAll({ limit: 500 });
      if (result.success) {
        setClients(result.data || []);
      }
    } catch (err) {
      console.error('Error loading clients:', err);
    }
  }, []);

  // Load projects function
  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage
      };
      
      // Server-side filtering
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      if (clientFilter !== 'all') {
        params.client_id = parseInt(clientFilter);
      }
      
      if (typeFilter !== 'all') {
        params.project_type = typeFilter;
      }
      
      // Server-side search
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      // Server-side sorting
      params.sort_by = sortField;
      params.sort_order = sortDirection;
      
      const result = await ProjectService.getAll(params);
      
      if (result.success) {
        setProjects(result.data || []);
        setTotalProjects(result.total || 0);
      } else {
        setError(result.error);
        setProjects([]);
        setTotalProjects(0);
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Σφάλμα φόρτωσης projects');
      setProjects([]);
      setTotalProjects(0);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, typeFilter, clientFilter, currentPage, itemsPerPage, sortField, sortDirection]);

  // Load data on mount
  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Helper function to get client name
  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : `Client ${clientId}`;
  };

  // Handle actions
  const handleRefresh = () => {
    loadProjects();
  };

  const handleCreate = () => {
    setSelectedProject(null);
    setCreateModalOpen(true);
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    setEditModalOpen(true);
  };

  const handleDelete = (project) => {
    setSelectedProject(project);
    setDeleteDialogOpen(true);
  };

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Form submission handlers
  const handleCreateSubmit = async (formData) => {
    try {
      const result = await ProjectService.create(formData);
      if (result.success) {
        setCreateModalOpen(false);
        loadProjects();
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: 'Σφάλμα δημιουργίας έργου' };
    }
  };

  const handleEditSubmit = async (formData) => {
    try {
      const result = await ProjectService.update(selectedProject.id, formData);
      if (result.success) {
        setEditModalOpen(false);
        loadProjects();
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: 'Σφάλμα ενημέρωσης έργου' };
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const result = await ProjectService.delete(selectedProject.id);
      if (result.success) {
        setDeleteDialogOpen(false);
        loadProjects();
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Σφάλμα διαγραφής έργου');
    }
  };

  // Mobile card renderer
  const renderProjectCard = (project) => {
    const statusLabels = {
      'active': 'Ενεργό',
      'inactive': 'Ανενεργό',
      'completed': 'Ολοκληρωμένο',
      'cancelled': 'Ακυρωμένο'
    };
    const statusColors = {
      'active': 'success',
      'inactive': 'warning',
      'completed': 'primary',
      'cancelled': 'error'
    };

    return (
      <ItemCard
        key={project.id}
        title={project.name}
        subtitle={null}
        headerActions={[
          <Chip 
            key="type"
            label={ProjectService.getTypeLabel(project.project_type)}
            color="info"
            variant="outlined"
            size="small"
          />,
          <Chip 
            key="status"
            label={statusLabels[project.status] || project.status}
            color={statusColors[project.status] || 'default'}
            size="small"
          />
        ]}
        basicInfo={[
          // Πελάτης
          {
            icon: <PersonIcon fontSize="small" color="action" />,
            text: getClientName(project.client_id)
          },
          // Domain/URL
          project.domain && {
            icon: <LanguageIcon fontSize="small" color="action" />,
            text: project.domain
          },
          // Project Type
          {
            icon: <WorkIcon fontSize="small" color="action" />,
            text: ProjectService.getTypeLabel(project.project_type)
          }
        ].filter(Boolean)}
        actions={[
          <IconButton
            key="edit"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(project);
            }}
            sx={{ color: 'primary.main' }}
            title="Επεξεργασία"
          >
            <EditIcon fontSize="small" />
          </IconButton>,
          <IconButton
            key="delete"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(project);
            }}
            sx={{ color: 'error.main' }}
            title="Διαγραφή"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ]}
      />
    );
  };

  // Table columns configuration
  const columns = [
    {
      field: 'project_type',
      headerName: 'Τύπος Έργου',
      sortable: true,
      minWidth: 120,
      renderCell: (value) => (
        <Chip
          label={ProjectService.getTypeLabel(value)}
          icon={<span>{ProjectService.getTypeIcon(value)}</span>}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'client_id',
      headerName: 'Πελάτης',
      sortable: true,
      minWidth: 150,
      renderCell: (value) => (
        <Typography variant="body2">
          {typeof value === 'object' ? (value?.name || 'Άγνωστος') : getClientName(value)}
        </Typography>
      )
    },
    {
      field: 'domain',
      headerName: 'Domain/URL',
      sortable: true,
      minWidth: 200,
      renderCell: (value) => (
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Κατάσταση',
      sortable: true,
      minWidth: 120,
      renderCell: (value) => (
        <Chip
          label={value === 'active' ? 'Ενεργό' : value === 'inactive' ? 'Ανενεργό' : value === 'completed' ? 'Ολοκληρωμένο' : value === 'cancelled' ? 'Ακυρωμένο' : value}
          color={value === 'active' ? 'success' : value === 'inactive' ? 'warning' : value === 'completed' ? 'primary' : value === 'cancelled' ? 'error' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Ενέργειες',
      sortable: false,
      minWidth: 100,
      renderCell: (value, row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleEdit(row)}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(row)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  // Search header actions with filters
  const searchActions = [
    <FormControl key="status" size="small" sx={{ minWidth: 120 }}>
      <InputLabel>Κατάσταση</InputLabel>
      <Select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        label="Κατάσταση"
      >
        <MenuItem value="all">Όλα</MenuItem>
        <MenuItem value="active">Ενεργά</MenuItem>
        <MenuItem value="completed">Ολοκληρωμένα</MenuItem>
        <MenuItem value="paused">Παυμένα</MenuItem>
      </Select>
    </FormControl>,
    
    <FormControl key="type" size="small" sx={{ minWidth: 120 }}>
      <InputLabel>Τύπος</InputLabel>
      <Select
        value={typeFilter}
        onChange={(e) => setTypeFilter(e.target.value)}
        label="Τύπος"
      >
        <MenuItem value="all">Όλα</MenuItem>
        <MenuItem value="website">Website</MenuItem>
        <MenuItem value="web_application">Web Application</MenuItem>
        <MenuItem value="other">Άλλο</MenuItem>
      </Select>
    </FormControl>,
    
    <FormControl key="client" size="small" sx={{ minWidth: 150 }}>
      <InputLabel>Πελάτης</InputLabel>
      <Select
        value={clientFilter}
        onChange={(e) => setClientFilter(e.target.value)}
        label="Πελάτης"
      >
        <MenuItem value="all">Όλοι</MenuItem>
        {clients.map(client => (
          <MenuItem key={client.id} value={client.id.toString()}>
            {client.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>,
    
    <Button
      key="refresh"
      variant="outlined"
      startIcon={<RefreshIcon />}
      onClick={handleRefresh}
      disabled={loading}
    >
      Ανανέωση
    </Button>
  ];

  // Page header actions
  const pageActions = [
    <IconButton
      key="create"
      color="primary"
      onClick={handleCreate}
      sx={{ 
        backgroundColor: 'primary.main',
        color: 'white',
        '&:hover': {
          backgroundColor: 'primary.dark',
        }
      }}
    >
      <AddIcon />
    </IconButton>
  ];

  // Client options for form
  const clientOptions = clients.map(client => ({
    value: client.id,
    label: client.name
  }));

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <PageHeader
        title="Διαχείριση Έργων"
        subtitle="Προβολή και διαχείριση όλων των έργων"
        icon="📋"
        actions={pageActions}
      />

      {/* Search and Filters */}
      <SearchHeader
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Αναζήτηση έργων (όνομα, πελάτης, domain, τύπος)..."
        sortField={sortField}
        sortDirection={sortDirection}
        onSortChange={handleSort}
        sortOptions={[
          { value: 'name', label: 'Όνομα' },
          { value: 'project_type', label: 'Τύπος' },
          { value: 'domain', label: 'Domain' },
          { value: 'status', label: 'Κατάσταση' },
          { value: 'created_at', label: 'Ημ. Δημιουργίας' },
        ]}
      />

      {/* Error Display */}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </Typography>
        </Box>
      )}

      {/* Data Table */}
      <ResponsiveTable
        data={projects}
        columns={columns}
        loading={loading}
        emptyMessage="Δεν βρέθηκαν έργα"
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        renderMobileCard={renderProjectCard}
        onRowClick={handleEdit}
      />

      {/* Pagination */}
      <PaginationComponent
        currentPage={currentPage}
        totalPages={Math.ceil(totalProjects / itemsPerPage)}
        totalItems={totalProjects}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Create Modal */}
      {clients.length > 0 && (
        <FormBuilder
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          schema={createProjectSchema}
          onSubmit={handleCreateSubmit}
          options={{ client_id: clientOptions }}
          modalProps={{
            title: 'Δημιουργία Νέου Έργου',
            maxWidth: 'md'
          }}
        />
      )}

      {/* Edit Modal */}
      {clients.length > 0 && (
        <FormBuilder
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          schema={updateProjectSchema}
          entity={selectedProject}
          onSubmit={handleEditSubmit}
          options={{ client_id: clientOptions }}
          modalProps={{
            title: 'Επεξεργασία Έργου',
            maxWidth: 'md'
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Διαγραφή Έργου"
        message="Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το έργο;"
        itemName={selectedProject?.name}
      />
    </Box>
  );
};

export default ProjectListingPage;
